import { MouseEvent, useEffect } from 'react';
import { useToggle } from 'react-use';
import { MultiSelectActionBar } from 'src/components/common/MultiSelect/MultiSelectActionBar';
import { InventoryGlobalBulkActions } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions';
import { SetInventoryFlattenedViewSelectionCountLabel } from 'src/components/Events/EventListing/InventoryEventListing/SetInventoryFlattenedViewSelectionCountLabel';
import { Content } from 'src/contexts/ContentContext';
import { NO_GROUP_ID } from 'src/contexts/MultiSelectionContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { ColumnSettingsModal } from 'src/modals/ColumnSettings/ColumnSettings';
import { LayoutIcon } from 'src/svgs';
import { CustomListingColumn } from 'src/utils/columns/inventory/inventoryCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionType } from 'src/utils/types/sectionType';
import { InventorySearchResult, UserSetting } from 'src/WebApiController';

import * as styles from './InventorySearchToolbar.css';

export const InventorySearchToolbar = ({
  disabled,
  setDisabled,
  activeSearchConfig,
  setLocalIsEventGroupingEnabled,
  localIsEventGroupingEnabled,
}: {
  disabled?: boolean;
  setDisabled?: (d: boolean) => void;
  activeSearchConfig?: InventorySearchResult;
  setLocalIsEventGroupingEnabled?: (enabled: boolean) => void;
  localIsEventGroupingEnabled?: boolean;
}) => {
  const [isColumnModalOpen, toggleColumnModal] = useToggle(false);

  const {
    value: storedInventoryFlattenedColumnsEnabledSetting,
    setUserSetting: setInventoryFlattenedColumnsEnabledSetting,
  } = useServerUserSetting<string[]>({
    id: UserSetting.InventoryFlattenedColumnsEnabled,
  });

  const {
    value: isEventGroupingEnabled,
    setUserSetting: setIsEventGroupingEnabled,
  } = useServerUserSetting<boolean>({
    id: UserSetting.GroupInventoryEventView,
  });

  const onColumnSettingButtonClickHandler = (
    e: MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();
    if (!isColumnModalOpen) toggleColumnModal(true);
  };

  // Quick workaround to disable event grouping if it's enabled from search V1. We want to 1) disable event grouping, and 2) bring back the event column in the flattened view.
  // See InventorySearchToolBar from search V1 for more details.
  // TODO: Implement in SAPI-3489
  useEffect(() => {
    if (isEventGroupingEnabled) {
      setInventoryFlattenedColumnsEnabledSetting([
        'Event',
        ...(storedInventoryFlattenedColumnsEnabledSetting || []),
      ]);

      setIsEventGroupingEnabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEventGroupingEnabled]);

  return (
    <Stack
      gap="m"
      alignItems="center"
      justifyContent="spaceBetween"
      className={styles.container}
    >
      <div style={{ flex: 1 }}>
        <SetInventoryFlattenedViewSelectionCountLabel>
          {!disabled && (
            <MultiSelectActionBar
              style={{ padding: 0 }}
              groupId={NO_GROUP_ID}
              displayMode="count-only"
              showHideOnSelection
            >
              <InventoryGlobalBulkActions />
            </MultiSelectActionBar>
          )}
        </SetInventoryFlattenedViewSelectionCountLabel>
      </div>

      <Stack gap="m">
        <Stack gap="m" alignItems="center" justifyContent="end">
          <Button
            variant="textPlain"
            style={{ paddingRight: vars.spacing.xxs }}
            onClick={onColumnSettingButtonClickHandler}
          >
            <LayoutIcon size={vars.iconSize.m} />
            <Content id={ContentId.Columns} />
          </Button>
        </Stack>
      </Stack>
      {isColumnModalOpen ? (
        <ColumnSettingsModal<CustomListingColumn>
          onClose={toggleColumnModal}
          sectionType={SectionType.ListingsFlattened}
        />
      ) : null}
    </Stack>
  );
};
